import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { getAdminPrompts, createPrompt, updatePrompt, deletePrompt, reorderPrompts } from "@/lib/checkout";
import { Loader2, RefreshCw, Plus, Trash2, ArrowUp, ArrowDown, Eye, EyeOff, Check, X } from "lucide-react";
import { useNoIndex } from "@/hooks/useNoIndex";
import { AdminGate } from "@/components/AdminGate";

export default function AdminPrompts() {
  useNoIndex();
  const [key, setKey] = useState("");
  const [entered, setEntered] = useState(() => localStorage.getItem("cc_admin_key") || "");
  const [prompts, setPrompts] = useState([]);
  const [currentId, setCurrentId] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [newQ, setNewQ] = useState("");
  const [newB, setNewB] = useState("");
  const [editId, setEditId] = useState(null);
  const [editQ, setEditQ] = useState("");
  const [editB, setEditB] = useState("");

  const load = async (k) => {
    setStatus("loading");
    setError("");
    try {
      const data = await getAdminPrompts(k);
      setPrompts(data.prompts || []);
      setCurrentId(data.current_prompt_id || null);
      setStatus("ready");
      localStorage.setItem("cc_admin_key", k);
    } catch (e) {
      setStatus("error");
      setError(e.message === "unauthorized" ? "That key is not valid." : "Could not load prompts. Try again.");
      if (e.message === "unauthorized") localStorage.removeItem("cc_admin_key");
    }
  };

  useEffect(() => {
    if (entered) load(entered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const submit = (e) => { e.preventDefault(); setEntered(key); load(key); };

  const add = async () => {
    if (!newQ.trim()) return;
    setBusy("add");
    try { await createPrompt(entered, { question: newQ, body: newB }); setNewQ(""); setNewB(""); await load(entered); } catch (e) { /* no-op */ }
    setBusy("");
  };

  const saveEdit = async (id) => {
    setBusy(id);
    try { await updatePrompt(entered, id, { question: editQ, body: editB }); setEditId(null); await load(entered); } catch (e) { /* no-op */ }
    setBusy("");
  };

  const toggleActive = async (p) => {
    setBusy(p.id);
    try { await updatePrompt(entered, p.id, { active: !p.active }); await load(entered); } catch (e) { /* no-op */ }
    setBusy("");
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this prompt from the rotation?")) return;
    setBusy(id);
    try { await deletePrompt(entered, id); await load(entered); } catch (e) { /* no-op */ }
    setBusy("");
  };

  const move = async (index, dir) => {
    const next = index + dir;
    if (next < 0 || next >= prompts.length) return;
    const ids = prompts.map((p) => p.id);
    [ids[index], ids[next]] = [ids[next], ids[index]];
    setBusy("reorder");
    try { await reorderPrompts(entered, ids); await load(entered); } catch (e) { /* no-op */ }
    setBusy("");
  };

  if (status !== "ready") {
    return (
      <AdminGate
        title="Weekly prompts"
        subtitle="Enter your admin key to manage the gentle prompt rotation."
        value={key}
        onChange={(e) => setKey(e.target.value)}
        onSubmit={submit}
        loading={status === "loading"}
        error={error}
        gateTestId="admin-prompts-gate"
        inputTestId="admin-prompts-key-input"
        errorTestId="admin-prompts-error"
        buttonTestId="admin-prompts-unlock"
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#0B132B] px-6 py-12" data-testid="admin-prompts-dashboard">
      <div className="mx-auto max-w-[760px]">
        <div className="flex items-center justify-between">
          <h1 className="font-heading text-3xl font-extrabold text-white">Weekly gentle prompts</h1>
          <button type="button" onClick={() => load(entered)} data-testid="admin-prompts-refresh"
            className="inline-flex items-center gap-2 rounded-xl border border-[#2A3A5E] px-4 py-2 text-sm font-semibold text-[#A8D5F5] hover:bg-white/5">
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>
        <p className="mt-2 text-sm text-[#7BA8C4]">One active prompt shows each week and changes every Monday. The rotation cycles through the full list in order before repeating. Changes take effect immediately, no deploy needed.</p>

        <div className="mt-8 rounded-2xl border border-[#1E2A47] bg-[#111A30] p-5" data-testid="admin-prompt-add">
          <h2 className="font-heading text-lg font-bold text-white">Add a prompt</h2>
          <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="Question (e.g. What needs less pressure this week?)"
            data-testid="admin-prompt-new-question" className="mt-3 border-[#2A3A5E] bg-[#0B132B] text-white" />
          <Input value={newB} onChange={(e) => setNewB(e.target.value)} placeholder="Supporting line (optional)"
            data-testid="admin-prompt-new-body" className="mt-2 border-[#2A3A5E] bg-[#0B132B] text-white" />
          <button type="button" onClick={add} disabled={busy === "add" || !newQ.trim()} data-testid="admin-prompt-add-button"
            className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#F5A623] px-4 py-2.5 font-heading text-sm font-bold text-[#0B132B] transition-transform duration-200 hover:-translate-y-0.5 disabled:opacity-60">
            {busy === "add" ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4" /> Add to rotation</>}
          </button>
        </div>

        <div className="mt-6 space-y-3">
          {prompts.length === 0 && <p className="text-[#7BA8C4]">No prompts yet. Add one above.</p>}
          {prompts.map((p, i) => (
            <div key={p.id} data-testid={`admin-prompt-${p.id}`}
              className={`rounded-2xl border p-4 ${p.id === currentId ? "border-[#F5A623] bg-[#1A1608]" : p.active ? "border-[#1E2A47] bg-[#111A30]" : "border-[#2A2A33] bg-[#14141A]"}`}>
              {editId === p.id ? (
                <div>
                  <Input value={editQ} onChange={(e) => setEditQ(e.target.value)} data-testid={`admin-prompt-edit-q-${p.id}`}
                    className="border-[#2A3A5E] bg-[#0B132B] text-white" />
                  <Input value={editB} onChange={(e) => setEditB(e.target.value)} data-testid={`admin-prompt-edit-b-${p.id}`}
                    className="mt-2 border-[#2A3A5E] bg-[#0B132B] text-white" />
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => saveEdit(p.id)} disabled={busy === p.id} data-testid={`admin-prompt-save-${p.id}`}
                      className="inline-flex items-center gap-1 rounded-lg bg-[#15816B] px-3 py-1.5 text-xs font-bold text-white hover:bg-[#126f5c]">
                      {busy === p.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Check className="h-3.5 w-3.5" /> Save</>}
                    </button>
                    <button type="button" onClick={() => setEditId(null)}
                      className="inline-flex items-center gap-1 rounded-lg border border-[#3A4A6E] px-3 py-1.5 text-xs font-semibold text-[#A8D5F5] hover:bg-white/5">
                      <X className="h-3.5 w-3.5" /> Cancel
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between gap-4">
                  <div className="min-w-0">
                    <p className="font-heading font-bold text-white">
                      <span className="mr-2 text-[#6E829B]">{i + 1}.</span>{p.question}
                      {p.id === currentId && <span className="ml-2 rounded bg-[#F5A623] px-2 py-0.5 text-[10px] font-bold text-[#0B132B]">This week</span>}
                      {!p.active && <span className="ml-2 rounded bg-[#3A3A45] px-2 py-0.5 text-[10px] font-bold text-[#B9C7D8]">Off</span>}
                    </p>
                    {p.body && <p className="mt-1 text-[13px] text-[#9FB2C9]">{p.body}</p>}
                  </div>
                  <div className="flex flex-shrink-0 items-center gap-1">
                    <button type="button" onClick={() => move(i, -1)} disabled={i === 0 || busy === "reorder"} data-testid={`admin-prompt-up-${p.id}`}
                      className="rounded-lg border border-[#2A3A5E] p-1.5 text-[#A8D5F5] hover:bg-white/5 disabled:opacity-30"><ArrowUp className="h-4 w-4" /></button>
                    <button type="button" onClick={() => move(i, 1)} disabled={i === prompts.length - 1 || busy === "reorder"} data-testid={`admin-prompt-down-${p.id}`}
                      className="rounded-lg border border-[#2A3A5E] p-1.5 text-[#A8D5F5] hover:bg-white/5 disabled:opacity-30"><ArrowDown className="h-4 w-4" /></button>
                    <button type="button" onClick={() => toggleActive(p)} disabled={busy === p.id} data-testid={`admin-prompt-toggle-${p.id}`}
                      className="rounded-lg border border-[#2A3A5E] p-1.5 text-[#A8D5F5] hover:bg-white/5">{p.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}</button>
                    <button type="button" onClick={() => { setEditId(p.id); setEditQ(p.question); setEditB(p.body || ""); }} data-testid={`admin-prompt-edit-${p.id}`}
                      className="rounded-lg border border-[#2A3A5E] px-2 py-1.5 text-xs font-semibold text-[#A8D5F5] hover:bg-white/5">Edit</button>
                    <button type="button" onClick={() => remove(p.id)} disabled={busy === p.id} data-testid={`admin-prompt-delete-${p.id}`}
                      className="rounded-lg border border-[#5A3A3A] p-1.5 text-[#FFC7C7] hover:bg-[#1A1414]"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
